import { format, isValid, parse } from 'date-fns';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  formatDaysRemaining,
  getDaysUntilSol,
  getSolSeverity,
  SOL_SEVERITY_STYLES,
} from '@/utils/solUtils';

import { useInlineEdit } from '../hooks/useInlineEdit';
import { InfoBadge } from './InfoBadge';
import { InlineEditControls } from './InlineEditControls';

interface SolEditBadgeProps {
  matterId: string;
  value: string | undefined | null;
  className?: string;
}

const formatDateForDisplay = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return '';

  try {
    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }

    const date = new Date(dateString);
    if (isValid(date)) {
      return format(date, 'MM/dd/yyyy');
    }
  } catch {
    // Silent catch
  }

  return dateString;
};

export const SolEditBadge: React.FC<SolEditBadgeProps> = ({
  matterId,
  value,
  className,
}) => {
  const {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  } = useInlineEdit({
    matterId,
    initialValue: value || '',
    fieldName: 'statute_of_limitations_date',
    onSuccessMessage: 'SOL date updated',
    transformValue: (val) => val || null,
  });

  const formattedDate = formatDateForDisplay(value);
  const daysUntilSol = getDaysUntilSol(value);
  const severity = getSolSeverity(value);

  let displayValue = 'Not set';
  if (formattedDate && daysUntilSol !== null) {
    displayValue = `${formattedDate} (${formatDaysRemaining(daysUntilSol)})`;
  } else if (formattedDate) {
    displayValue = formattedDate;
  }

  return (
    <Popover
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) handleCancel();
        else setIsEditing(true);
      }}
    >
      <PopoverTrigger asChild>
        <InfoBadge
          label="SOL"
          value={displayValue}
          onClick={() => setIsEditing(true)}
          showLabel
          className={`border ${formattedDate ? SOL_SEVERITY_STYLES.chip[severity] : SOL_SEVERITY_STYLES.chip.none} hover:opacity-80 ${className || ''}`}
        />
      </PopoverTrigger>
      <PopoverContent className="p-2 w-auto" align="start" side="bottom">
        <InlineEditControls
          isEditing={isEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={isLoading}
        >
          <div className="flex items-center gap-1">
            <span className="text-xs text-zinc-500 whitespace-nowrap">
              SOL:
            </span>
            <Input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="h-6 text-xs w-36 px-1 py-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            {editValue && (
              <button
                type="button"
                onClick={() => setEditValue('')}
                className="text-xs text-zinc-500 hover:text-red-600 whitespace-nowrap transition-colors"
                title="Clear SOL date"
              >
                Clear
              </button>
            )}
          </div>
        </InlineEditControls>
      </PopoverContent>
    </Popover>
  );
};
